import { Icon } from "@iconify/react";
import { ChevronUp } from "lucide-react";
import { memo, type ReactNode, useId, useLayoutEffect, useRef, useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MessageScrollerItem, useMessageScroller } from "@/components/ui/message-scroller";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import type { TicketEventGroup, TicketEventItem } from "../utils/ticket-activity-groups";
import { TicketResolutionActivity } from "./ticket-resolution-activity";

export const TicketEventActivity = memo(({ activity, actions }: { activity: TicketEventItem; actions?: ReactNode }) => {
  if (activity.resolution) return <TicketResolutionActivity activity={{ ...activity, resolution: activity.resolution }} />;

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 py-2">
      <div className="flex min-w-0 max-w-full items-center gap-2 text-xs text-muted-foreground">
        <Icon className="size-3.5 shrink-0" icon={activity.icon} />
        <span className="truncate">
          {activity.segments.map((segment, index) =>
            segment.emphasis ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: 文案分段没有稳定标识，位置即身份
              <span className="text-foreground" key={`${activity.id}-${index}`}>
                {segment.text}
              </span>
            ) : (
              segment.text
            ),
          )}
        </span>
        <span aria-hidden="true">·</span>
        <time className="shrink-0">{activity.time}</time>
      </div>
      {actions}
    </div>
  );
});

export function TicketEventActivityGroup({ group }: { group: TicketEventGroup }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const triggerId = useId();
  const contentId = useId();
  const summaryRef = useRef<HTMLDivElement>(null);
  const pendingPositionRef = useRef<{ expanded: boolean; scrollMargin: number | null } | null>(null);
  const { scrollToMessage } = useMessageScroller();
  const latestEvent = group.events.at(-1);
  const earlierEvents = group.events.slice(0, -1);

  useLayoutEffect(() => {
    const position = pendingPositionRef.current;
    if (!position || position.expanded !== expanded) return;
    pendingPositionRef.current = null;

    // 历史记录在摘要上方展开，仍由滚动组件将摘要保留在原来的阅读位置。
    if (position.scrollMargin !== null) {
      scrollToMessage(triggerId, { align: "start", scrollMargin: position.scrollMargin, behavior: "auto" });
    } else {
      // 窄屏由外层页面滚动，只保证操作入口仍在可见区域。
      summaryRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" });
    }
  }, [expanded, scrollToMessage, triggerId]);

  if (!latestEvent) return null;

  return (
    <Collapsible
      open={expanded}
      onOpenChange={(open) => {
        // 手动查看历史时先退出自动跟底，避免展开后把入口与前面的消息顶出视窗。
        scrollToMessage(triggerId, { align: "nearest", behavior: "auto" });
        const summary = summaryRef.current;
        const viewport = summary?.closest<HTMLElement>('[data-slot="message-scroller-viewport"]');
        if (summary) {
          pendingPositionRef.current = {
            expanded: open,
            scrollMargin:
              viewport && viewport.scrollHeight > viewport.clientHeight ? summary.getBoundingClientRect().top - viewport.getBoundingClientRect().top : null,
          };
        }
        setExpanded(open);
      }}
    >
      <CollapsibleContent id={contentId}>
        <div className="flex flex-col gap-1 pb-1">
          {earlierEvents.map((activity) => (
            <TicketEventActivity activity={activity} key={activity.id} />
          ))}
        </div>
      </CollapsibleContent>
      <MessageScrollerItem messageId={triggerId} ref={summaryRef}>
        <TicketEventActivity
          activity={latestEvent}
          actions={
            <CollapsibleTrigger
              aria-controls={contentId}
              className="inline-flex shrink-0 items-center gap-0.5 rounded-sm text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              type="button"
            >
              {t(expanded ? "tickets.activity.collapseEvents" : "tickets.activity.expandEvents", { count: group.events.length })}
              <ChevronUp aria-hidden="true" className={cn("size-3 transition-transform", expanded && "rotate-180")} />
            </CollapsibleTrigger>
          }
        />
      </MessageScrollerItem>
    </Collapsible>
  );
}
